// Imports
import React, { useEffect, useState } from "react";
import { message } from "antd";
import CreateContract from "../CreateContract/CreateContract";
import axios from "../../axios/AxiosInstance";
import { apiErrorMessage } from "../../utils/apiError";
import { DndContext, PointerSensor, useSensor, useSensors, DragOverlay, useDroppable, pointerWithin } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Copy, Edit, X, Check, Clock, Users, CheckCircle, Send, XCircle, Mail, Phone, MapPin, Calendar, Briefcase, Euro, Hash, Building, FileText, Loader2 } from "lucide-react";
import { Button } from "../../components/reusableComponents/Button";
import VacationInvoiceModal from "./VacationInvoiceModal";

// InfoItem
const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 mt-1 text-indigo-300 flex-shrink-0" />
    <div className="flex flex-col">
      <span className="text-xs text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-800">{value}</span>
    </div>
  </div>
);

// Draggable card
const DraggableCandidateCard = ({ candidate }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: String(candidate._id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="group flex items-center justify-between p-2.5 rounded-lg transition-all duration-200 hover:bg-white/10 cursor-move"
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 uppercase rounded-full bg-black p-1 flex items-center justify-center font-bold text-sm text-white border-2 ">
          {candidate.firstname?.[0]}
          {candidate.lastname?.[0]}
        </div>
        <span className="text-sm capitalize font-medium text-black group-hover:text-gray-700">
          {candidate.firstname} {candidate.lastname}
        </span>
      </div>
    </div>
  );
};

const STATUSES = [
  "candidates",
  "contactedCandidates",
  "acceptedCandidates",
  "refusedByAgence",
  "submittedToClient",
  "confirmedCandidates",
];

const STATUSES_TITLES = [
  "candidat(s) disponible(s)",
  "candidat(s) contacté(s)",
  "candidat(s) accepté(s)",
  "refusé(s) par l'agence",
  "soumis au client",
  "candidat(s) confirmé(s)",
];

const normalizedText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

export const normalizeOrderContractType = (value) => {
  const type = normalizedText(value);
  if (type.includes("interim")) return "interim";
  if (type.includes("vacation")) return "vacation";
  if (/(^|\W)cdd($|\W)/.test(type)) return "cdd";
  if (/(^|\W)cdi($|\W)/.test(type)) return "cdi";
  return "";
};

const validDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const timeDate = (value) => {
  const match = String(value || "").match(/^(\d{1,2}):?(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return new Date(2000, 0, 1, hours, minutes);
};

export const buildOrderContractDraft = (
  order,
  candidate,
  client,
  qualifications = [],
) => {
  const contractType = normalizeOrderContractType(
    order.typeContrat || order.contractType,
  );
  const firstPeriod = order.dateRanges?.[0] || {};
  const qualificationName = String(order.qualification || "").trim();
  const qualificationKey = normalizedText(qualificationName);
  const clientQualification =
    qualifications.find((qualification) =>
      [
        qualification?.TxtQualification,
        qualification?.IdQualification,
        qualification?.profession,
      ].some((value) => normalizedText(value) === qualificationKey),
    ) ||
    (qualificationName
      ? {
          IdQualification: "",
          TxtQualification: qualificationName,
          TauxPaye: order.hourlyRate || "",
        }
      : undefined);

  return {
    candidate,
    contractType,
    contact: order.contact || "",
    startDate: validDate(firstPeriod.startDate || order.startDate),
    endDate:
      contractType === "cdi"
        ? null
        : validDate(firstPeriod.endDate || order.endDate || order.startDate),
    startHour: timeDate(firstPeriod.startHour || order.startHour),
    endHour: timeDate(firstPeriod.endHour || order.endHour),
    motif: order.motif || "",
    justificatif: order.justificatif || "",
    nbHeureHebdo: order.nbHeureHebdo || "",
    task: order.task || "",
    address: order.address || "",
    postCode: order.postCode || "",
    town: order.town || "",
    clientName:
      order.clientName ||
      order.companyName ||
      client.companyName ||
      client.denomination ||
      "",
    clientQualification,
    consigne: order.consigne || "0 : Aucune",
    TabPrimes: Array.isArray(order.primes) ? order.primes : [],
    tauxHoraire: order.hourlyRate || "",
    isEditTauxHoraire: Boolean(order.hourlyRate),
    coefficient: order.coefficient || "",
    Reference1: order.orderNumber || "",
    PenibiliteNC: order.PenibiliteNC || "",
    EstChezClient: order.EstChezClient || "",
    EstBTP: order.EstBTP || "",
    NbJourEssai: order.NbJourEssai || "",
    dateRanges: Array.isArray(order.dateRanges) ? order.dateRanges : [],
    orderId: order._id,
  };
};

const CandidateColumn = ({ title, status, candidates, showCv, selectable, selectedIds, onToggle, onSendEmail }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `column-${status}` });

  const textColor = isOver ? "text-indigo-600" : "text-gray-800";
  const borderColor = isOver ? "border-indigo-400" : "border-gray-200";
  const bgColor = isOver ? "bg-indigo-50" : "bg-white";

  return (
    <div
      ref={setNodeRef}
      className={`transition-colors duration-200 ${bgColor} border ${borderColor} rounded-xl flex flex-col h-full`}
    >
      <div className="p-3 border-b border-gray-100 bg-gray-50 rounded-t-xl">
        <h4 className={`text-sm font-semibold text-center ${textColor}`}>
          {title}
        </h4>
      </div>
      <div className="p-3 space-y-3 overflow-y-auto flex-1 min-h-[150px]">
        <SortableContext
          items={(candidates || []).map((c) => String(c._id))}
          strategy={verticalListSortingStrategy}
        >
          {candidates?.length > 0 ? (
            candidates.map((c) => (
              <div
                className="flex group items-center justify-between"
                key={`${status}-${c._id}`}
              >
                {selectable && (
                  <input
                    type="checkbox"
                    className="mr-1"
                    aria-label={`Sélectionner ${c.firstname || ""} ${c.lastname || ""}`}
                    checked={selectedIds?.has(String(c._id))}
                    onChange={() => onToggle?.(String(c._id))}
                    onPointerDown={(event) => event.stopPropagation()}
                  />
                )}
                <DraggableCandidateCard candidate={c} />
                <div className="flex items-center gap-1 text-black">
                  <button
                    type="button"
                    aria-label={`Voir le CV de ${c.firstname || ""} ${c.lastname || ""}`}
                    onClick={(e) => showCv(e, c)}
                    onPointerDown={(event) => event.stopPropagation()}
                    className="p-1 hover:bg-white/20 rounded"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                  {onSendEmail && (
                    <button
                      type="button"
                      aria-label={`Proposer la mission à ${c.firstname || ""} ${c.lastname || ""}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onSendEmail(c);
                      }}
                      onPointerDown={(event) => event.stopPropagation()}
                      className="p-1 hover:bg-white/20 rounded"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-xs text-gray-400 pt-4">
              Aucun candidat
            </p>
          )}
        </SortableContext>
      </div>
    </div>
  );
};

const StatusStep = ({
  icon: Icon,
  title,
  count,
  isActive,
  isFirst,
  isLast,
}) => (
  <div className="relative flex-1 flex flex-col pb-3 items-center text-center">
    {!isFirst && (
      <div
        className={`absolute top-1/2 left-0 w-1/2 h-0.5 -translate-y-1/2 ${isActive ? "bg-indigo-500" : "bg-gray-300"}`}
      />
    )}
    {!isLast && (
      <div
        className={`absolute top-1/2 right-0 w-1/2 h-0.5 -translate-y-1/2 ${isActive ? "bg-indigo-500" : "bg-gray-300"}`}
      />
    )}
    <div
      className={`relative z-10 w-12 h-12 flex items-center justify-center rounded-full border-2 ${isActive ? "bg-indigo-500 text-white border-indigo-400 shadow-md" : "bg-white text-gray-400 border-gray-300"}`}
    >
      <Icon className="w-6 h-6" />
    </div>
    <p
      className={`mt-3 text-xs font-semibold ${isActive ? "text-gray-800" : "text-gray-400"}`}
    >
      {title}
    </p>
    <p className={`text-xs ${isActive ? "text-indigo-500" : "text-gray-400"}`}>
      {count} candidats
    </p>
  </div>
);

export const OrderDetails = ({
  commande: initialCommande,
  onClose,
  onDuplicate,
  onEdit,
}) => {
  const [commande, setCommande] = useState(initialCommande);
  const [activeCandidate, setActiveCandidate] = useState(null);
  const [contractRequest, setContractRequest] = useState(null);
  const [preparingCandidateId, setPreparingCandidateId] = useState(null);
  const [contractError, setContractError] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("plus de besoin");
  const [cancelling, setCancelling] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [preparingInvoiceCandidateId, setPreparingInvoiceCandidateId] =
    useState(null);
  const [invoiceError, setInvoiceError] = useState("");

  useEffect(() => {
    setCommande(initialCommande);
    setSelectedIds(new Set());
  }, [initialCommande]);

  const prepareContract = async candidate => {
    if (preparingCandidateId) return;
    const candidateId = candidate?._id;
    if (!candidateId) {
      const detail = "Le candidat confirmé est invalide.";
      setContractError(detail);
      message.error(detail);
      return;
    }
    setPreparingCandidateId(String(candidateId));
    setContractError("");
    setContractRequest(null);
    try {
      const id = commande.client?._id || commande.client;
      if (!id) throw new Error("Le client de cette commande est introuvable");
      const { data: client } = await axios.get(`client/${id}`);
      if (!client?._id)
        throw new Error("Le client de cette commande est introuvable");

      let qualifications = [];
      let chantiers = [];
      if (client.pld != null && String(client.pld).trim()) {
        const { data } = await axios.get(`client/qualifications/${client.pld}`);
        qualifications = Array.isArray(data?.TabQualifications)
          ? data.TabQualifications
          : [];
        chantiers = Array.isArray(data?.TabChantiers)
          ? data.TabChantiers
          : [];
      }

      const initial = buildOrderContractDraft(
        commande,
        candidate,
        client,
        qualifications,
      );
      if (!initial.contractType)
        throw new Error("Le type de contrat de la commande n’est pas reconnu");
      setContractRequest({
        client: { ...client, contacts: client.contacts || [] },
        initial,
        qualifications,
        chantiers,
      });
    } catch (error) {
      const detail = apiErrorMessage(
        error,
        "Impossible de préparer le contrat. Vérifiez les informations de la commande",
      );
      if (detail) {
        setContractError(detail);
        message.error(detail);
      }
    } finally {
      setPreparingCandidateId(null);
    }
  };
  const openInvoice = async (candidate) => {
    if (preparingInvoiceCandidateId) return;
    const candidateId = candidate?._id;
    if (!candidateId) {
      const detail = "Le candidat confirmé est invalide.";
      setInvoiceError(detail);
      message.error(detail);
      return;
    }
    setPreparingInvoiceCandidateId(String(candidateId));
    setInvoiceError("");
    setInvoice(null);
    try {
      const id = commande.client?._id || commande.client;
      if (!id) throw new Error("Le client de cette commande est introuvable");
      const { data: client } = await axios.get(`client/${id}`);
      if (!client?._id)
        throw new Error("Le client de cette commande est introuvable");
      setInvoice({ candidate, client });
    } catch (error) {
      const detail = apiErrorMessage(error, "Impossible d’ouvrir la facture.");
      if (detail) {
        setInvoiceError(detail);
        message.error(detail);
      }
    } finally {
      setPreparingInvoiceCandidateId(null);
    }
  };

  const candidateHasCreatedDocument = (candidate) =>
    (commande.contractedCandidates || []).some(
      (entry) =>
        String(entry?._id || entry) === String(candidate?._id || candidate),
    );

  const invoiceCreated = () => {
    const candidate = invoice?.candidate;
    if (!candidate) return;
    setCommande((current) => ({
      ...current,
      contractedCandidates: [
        ...(current.contractedCandidates || []).filter(
          (entry) =>
            String(entry?._id || entry) !== String(candidate._id),
        ),
        candidate,
      ],
      status: "Contrat créé",
    }));
    setInvoice(null);
    setInvoiceError("");
  };
  const propose = async (ids) => {
    const list = [...ids];
    if (!list.length) {
      message.error("Sélectionnez au moins un candidat.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post("order/email/bulk", { orderId: commande._id, candidateIds: list });
      const ok = new Set((data?.okIds || []).map(String));
      if (!ok.size) {
        message.error("Aucune proposition n’a pu être envoyée.");
        return;
      }
      setCommande((prev) => {
        const moved = (prev.candidates || []).filter((candidate) => ok.has(String(candidate._id)));
        return {
          ...prev,
          status: "Candidat(s) contacté(s)",
          candidates: (prev.candidates || []).filter((candidate) => !ok.has(String(candidate._id))),
          contactedCandidates: [...moved, ...(prev.contactedCandidates || [])],
        };
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ok.forEach((id) => next.delete(id));
        return next;
      });
      message.success(data.failed ? `${data.sent} proposition(s) envoyée(s), ${data.failed} en échec.` : `${data.sent} proposition(s) envoyée(s).`);
    } catch (error) {
      const detail = apiErrorMessage(error, "Les propositions n’ont pas pu être envoyées.");
      if (detail) message.error(detail);
    } finally {
      setSending(false);
    }
  };
  const cancelOrder = async () => {
    if (!cancelReason.trim() || cancelling) return;
    setCancelling(true);
    try {
      await axios.post(`order/${commande._id}/cancel`, { reason: cancelReason });
      message.success("Commande annulée.");
      onClose?.();
    } catch (error) {
      const detail = apiErrorMessage(error, "Impossible d’annuler la commande.");
      if (detail) message.error(detail);
    } finally {
      setCancelling(false);
    }
  };

  // ✅ États pour le CV
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [cvData, setCvData] = useState(null);
  const [cvType, setCvType] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalCV, setIsModalCV] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  const getStatusBadge = () => {
    const isComplete =
      commande.confirmedCandidates?.length >= commande.quantity ||
      commande.status === "terminee";
    const status = isComplete ? "Complète" : "Incomplète";
    const color = isComplete
      ? "bg-green-100 text-green-700"
      : "bg-yellow-100 text-yellow-700";
    return (
      <span
        className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${color}`}
      >
        {status}
      </span>
    );
  };

  const handleDragStart = (event) => {
    const candidate = STATUSES.flatMap((status) => commande[status] || []).find(
      (c) => String(c._id) === String(event.active.id),
    );
    setActiveCandidate(candidate);
  };

  const handleDragOver = () => {};

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    let sourceStatus = null;
    let targetStatus = null;

    for (let status of STATUSES) {
      if ((commande[status] || []).find((c) => String(c._id) === activeId)) {
        sourceStatus = status;
      }
      if ((commande[status] || []).find((c) => String(c._id) === overId)) {
        targetStatus = status;
      }
    }

    if (overId.startsWith("column-")) {
      const columnStatus = overId.replace("column-", "");
      if (STATUSES.includes(columnStatus)) {
        targetStatus = columnStatus;
      }
    }

    if (!sourceStatus || !targetStatus) return;

    const candidate = commande[sourceStatus].find(
      (c) => String(c._id) === activeId,
    );
    if (!candidate) return;

    setCommande((prev) => {
      const updated = { ...prev };
      updated[sourceStatus] = prev[sourceStatus].filter(
        (c) => String(c._id) !== activeId,
      );

      const targetItems = [...(prev[targetStatus] || [])];
      const overIndex = targetItems.findIndex((c) => String(c._id) === overId);

      if (overId.startsWith("column-") || overIndex === -1) {
        targetItems.unshift(candidate);
      } else {
        targetItems.splice(overIndex, 0, candidate);
      }

      updated[targetStatus] = targetItems;
      console.log(updated);
      return updated;
    });

    setActiveCandidate(null);
  };

  const statusSteps = [
    {
      title: "Disponibles",
      icon: Users,
      count: commande.candidates?.length || 0,
    },
    {
      title: "Contactés",
      icon: Clock,
      count: commande.contactedCandidates?.length || 0,
    },
    {
      title: "Accepté",
      icon: CheckCircle,
      count: commande.acceptedCandidates?.length || 0,
    },
    {
      title: "Proposé",
      icon: Send,
      count: commande.submittedToClient?.length || 0,
    },
    {
      title: "Confirmé",
      icon: Check,
      count: commande.confirmedCandidates?.length || 0,
    },
  ];

  // ✅ Fonction showCv
  const showCv = (e, candidate) => {
    e.stopPropagation();
    const candidateId = candidate._id;
    setLoadingSearch(true);
    axios
      .get(`cv/${candidateId}`)
      .then((res) => {
        const base64 = res.data.data;
        setCvData(base64);
        const header = base64.substring(0, 5);

        if (header === "JVBER") {
          setCvType("pdf");
        } else if (header.startsWith("/9j/")) {
          setCvType("jpeg");
        } else if (header.startsWith("iVBOR")) {
          setCvType("png");
        } else {
          console.log("Unsupported file type");
        }

        setSelectedUser(candidate);
        setIsModalCV(true);
      })
      .catch((e) => console.log(e))
      .finally(() => setLoadingSearch(false));
  };

  const activeStepIndex = statusSteps
    .slice()
    .reverse()
    .findIndex((step) => step.count > 0);
  const currentStep =
    activeStepIndex !== -1 ? statusSteps.length - 1 - activeStepIndex : -1;

  return (
    <div className="w-full mx-auto p-8 bg-white rounded-3xl border border-gray-200 shadow-xl">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center pb-6 mb-6 border-b border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            {commande.typeContrat} {getStatusBadge()}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Recruteur :{" "}
            <span className="font-medium text-gray-700">
              {commande.recruiter?.firstname || "-"}
            </span>
          </p>
        </div>
        <Button
          variant="ghost"
          onClick={onClose}
          className="text-gray-500 hover:text-black"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Status steps */}
      <div className="flex justify-between items-start gap-4 mb-10">
        {statusSteps.map((step, i) => (
          <StatusStep
            key={i}
            {...step}
            isActive={i <= currentStep}
            isFirst={i === 0}
            isLast={i === statusSteps.length - 1}
          />
        ))}
      </div>

      {/* Infos */}
      <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-6">
          Informations de la commande
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8">
          <div className="flex flex-col gap-4">
            <InfoItem
              icon={Building}
              label="Client"
              value={commande.client?.companyName}
            />
            <InfoItem
              icon={Users}
              label="Contact"
              value={commande.contactData?.name}
            />
            <InfoItem
              icon={Phone}
              label="Téléphone"
              value={commande.contactData?.phone}
            />
            <InfoItem
              icon={Mail}
              label="Email"
              value={commande.contactData?.email}
            />
          </div>
          <div className="flex flex-col gap-4">
            <InfoItem
              icon={Briefcase}
              label="Type de contrat"
              value={commande.typeContrat}
            />
            <InfoItem
              icon={Calendar}
              label="Date de début"
              value={`${new Date(commande.startDate).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}`}
            />
            <InfoItem
              icon={Clock}
              label="Horaires"
              value={`${commande.startHour} - ${commande.endHour}`}
            />
            <InfoItem
              icon={Calendar}
              label="Jours travaillés"
              value={(commande.workedDays || []).join(", ")}
            />
          </div>
          <div className="flex flex-col gap-4">
            <InfoItem icon={MapPin} label="Lieu" value={commande.town} />
            <InfoItem
              icon={Briefcase}
              label="Poste"
              value={commande.qualification}
            />
            <InfoItem icon={Hash} label="Quantité" value={commande.quantity} />
            <InfoItem icon={Users} label="EPI" value={commande.epi} />
          </div>
          <div className="flex flex-col gap-4">
            <InfoItem
              icon={Euro}
              label="Taux horaire"
              value={commande.hourlyRate}
            />
            <InfoItem icon={Euro} label="Primes" value={commande.bonus} />
            <div className="flex items-start gap-3">
              <CheckCircle className="w-4 h-4 mt-1 text-indigo-300 flex-shrink-0" />
              <div className="flex flex-col">
                <span className="text-xs text-gray-400">Tâches</span>
                <p className="text-sm font-medium text-gray-800 whitespace-pre-wrap">
                  {commande.task}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(commande.confirmedCandidates || []).length > 0 && (
        <section className="my-6 rounded-xl border border-blue-200 bg-blue-50 p-5">
          <h2 className="text-lg font-semibold text-slate-900">
            Candidats confirmés : préparer les documents
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            {normalizeOrderContractType(
              commande.typeContrat || commande.contractType,
            ) === "vacation"
              ? "Préparez le contrat ou créez directement le contrat de vacation et sa facture à faire."
              : "Préparez le contrat à partir des informations de la commande."}
          </p>
          {contractError && (
            <p role="alert" className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {contractError}
            </p>
          )}
          {invoiceError && (
            <p role="alert" className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {invoiceError}
            </p>
          )}
          <div className="mt-4 space-y-3">
            {commande.confirmedCandidates.map((candidate) => {
              const created = candidateHasCreatedDocument(candidate);
              const loadingInvoice =
                preparingInvoiceCandidateId === String(candidate._id);
              return (
                <div
                  key={candidate._id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white p-3"
                >
                  <strong className="text-sm text-slate-800">
                    {candidate.firstname} {candidate.lastname}
                  </strong>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={
                        created ||
                        Boolean(preparingCandidateId) ||
                        Boolean(preparingInvoiceCandidateId)
                      }
                      onClick={() => prepareContract(candidate)}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    >
                      {preparingCandidateId === String(candidate._id) ? (
                        <>
                          <Loader2 size={15} className="animate-spin" />
                          Préparation…
                        </>
                      ) : created ? (
                        "Document créé"
                      ) : (
                        "Préparer le contrat"
                      )}
                    </button>
                    {normalizeOrderContractType(
                      commande.typeContrat || commande.contractType,
                    ) === "vacation" && (
                      <button
                        type="button"
                        disabled={
                          created ||
                          Boolean(preparingCandidateId) ||
                          Boolean(preparingInvoiceCandidateId)
                        }
                        onClick={() => openInvoice(candidate)}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-blue-700 disabled:opacity-50"
                      >
                        {loadingInvoice ? (
                          <>
                            <Loader2 size={15} className="animate-spin" />
                            Ouverture…
                          </>
                        ) : created ? (
                          "Facture créée"
                        ) : (
                          "Faire la facture"
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
      {contractRequest && <CreateContract client={contractRequest.client} initialContract={contractRequest.initial} fixedCandidate qualifications={contractRequest.qualifications} chantiers={contractRequest.chantiers} isCreateContract setIsCreateContract={value => { if (!value) { setContractRequest(null); setContractError(""); } }} />}

      {commande.dateRanges?.length > 1 && <section className="my-5 rounded-xl border border-slate-200 bg-white p-4"><h2 className="mb-3 font-semibold text-slate-900">Dates de la commande</h2><div className="flex flex-wrap gap-2">{commande.dateRanges.map((period,index) => <span key={index} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{period.startDate}{period.endDate !== period.startDate && ` → ${period.endDate}`}{period.startHour && ` · ${period.startHour}–${period.endHour}`}</span>)}</div></section>}
      {(commande.candidates || []).length > 0 && (
        <div className="mt-8 flex flex-wrap items-center gap-2">
          <button type="button" disabled={sending || !selectedIds.size} onClick={() => propose(selectedIds)} className="rounded-lg bg-blue-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">Envoyer à la sélection ({selectedIds.size})</button>
          <button type="button" disabled={sending} onClick={() => propose((commande.candidates || []).map((candidate) => String(candidate._id)))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-blue-700 disabled:opacity-50">Envoyer à tout le monde ({commande.candidates.length})</button>
        </div>
      )}
      {/* Drag and Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
      >
        <div className="my-10 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 min-h-[300px]">
          {STATUSES.map((status, idx) => (
            <CandidateColumn
              key={status}
              title={STATUSES_TITLES[idx]}
              status={status}
              candidates={commande[status]}
              showCv={showCv}
              selectable={status === "candidates"}
              selectedIds={selectedIds}
              onToggle={(id) => setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })}
              onSendEmail={status === "candidates" ? (candidate) => propose([String(candidate._id)]) : undefined}
            />
          ))}
        </div>
        <DragOverlay>
          {activeCandidate ? (
            <DraggableCandidateCard candidate={activeCandidate} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Footer actions */}
      <div className="flex justify-end gap-3 mt-8 flex-wrap">
        <Button type="button" variant="outline" size="sm" onClick={() => onDuplicate?.(commande)}>
          <Copy className="w-4 h-4 mr-2" /> Dupliquer
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => onEdit?.(commande)}>
          <Edit className="w-4 h-4 mr-2" /> Modifier
        </Button>
        <Button variant="destructive" size="sm" onClick={() => setCancelOpen(true)} disabled={cancelling}>
          <XCircle className="w-4 h-4 mr-2" /> Annuler
        </Button>
        <Button
          size="sm"
          className="bg-indigo-600 text-white hover:bg-indigo-700"
        >
          <Check className="w-4 h-4 mr-2" /> Terminer
        </Button>
      </div>

      {invoice && (
        <VacationInvoiceModal
          commande={commande}
          candidate={invoice.candidate}
          client={invoice.client}
          onCreated={invoiceCreated}
          onClose={() => setInvoice(null)}
        />
      )}
      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6">
            <h2 className="text-lg font-bold text-slate-900">Annuler la commande</h2>
            <label className="mt-4 block text-sm text-slate-700">
              Motif
              <select value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2">
                {["plus de besoin", "concurrence", "trop cher", "ne repond plus", "pas de candidat à proposer", "atradius refusé"].map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setCancelOpen(false)} disabled={cancelling} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">Fermer</button>
              <button type="button" onClick={cancelOrder} disabled={cancelling} className="rounded-lg bg-rose-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">{cancelling ? "Annulation…" : "Confirmer l’annulation"}</button>
            </div>
          </div>
        </div>
      )}
      {/* ✅ Modal de visualisation du CV */}
      {isModalCV && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white w-[1000px] h-[800px] p-6 rounded-xl  pb-20">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">
                CV de {selectedUser?.firstname} {selectedUser?.lastname}
              </h2>
              <button
                onClick={() => setIsModalCV(false)}
                className="text-gray-600 hover:text-black"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {loadingSearch ? (
              <p className="text-center">Chargement...</p>
            ) : cvType === "pdf" ? (
              <iframe
                title="CV"
                src={`data:application/pdf;base64,${cvData}`}
                className="w-full h-full"
              />
            ) : cvType === "jpeg" || cvType === "png" ? (
              <img
                src={`data:image/${cvType};base64,${cvData}`}
                alt="CV"
                className="w-full"
              />
            ) : (
              <p className="text-center text-red-500">
                Format de CV non supporté
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
